import { Component, Input, OnInit, inject } from '@angular/core';
import { FeatureAccessService } from '@app/services/feature-access/feature-access.service';
import { LockableTabItem } from '@app/services/feature-access/feature-access.model';
import { FormArray, FormControl, FormGroup, UntypedFormGroup } from '@angular/forms';
import { DynamicRule, RuleEntity } from '@pdqdev/pdq-shared/dist/entities/Rules';
import { SubscriptionSelectorType } from '../../config';

enum CartControlFormTab {
  SETTINGS = 'settings',
  CONFIGURATIONS = 'configurations',
  DISPLAY_CONDITIONS = 'display-conditions',
  INSTALLATION = 'installation',
}

export interface TypedRuleGroup {
  entity: FormControl<string | null>;
  entityOperator: FormControl<string | null>;
  entityParams: UntypedFormGroup;
  groupIndex?: FormControl<number | undefined | null>;
}

export interface ExceptionSubscriptionSelectorFormGroup {
  type: FormControl<SubscriptionSelectorType>;
  upgradeButtonLabel: FormControl<string>;
  upgradeButtonAppearance: FormControl<string>;
  upgradeButtonTextColor: FormControl<string>;
}

export interface ExceptionItemConfigurationsFormGroup {
  changeVariant: FormControl<boolean>;
  editQuantity: FormControl<boolean>;
  removeItems: FormControl<boolean>;
  showSubscriptionSelector: FormControl<boolean>;
  subscriptionSelector: FormGroup<ExceptionSubscriptionSelectorFormGroup>;
}

// PDQ-7681 — one rule-based Cart Control exception: rule conditions (same builder as display
// conditions) + a per-exception "Customers can:" action override. `source` is set when the
// exception was scaffolded from a quick-add chip (omitted for hand-built ones).
export interface ExceptionFormGroup {
  id: FormControl<string>;
  source: FormControl<string | null>;
  conditions: FormArray<FormGroup<TypedRuleGroup>>;
  actions: FormGroup<ExceptionItemConfigurationsFormGroup>;
}

export interface ConfigurationsFormGroup {
  buttonLabel: FormControl<string>;
  buttonAppearance: FormControl<string>;
  changeVariant: FormControl<boolean>;
  editQuantity: FormControl<boolean>;
  removeItems: FormControl<boolean>;
  showSubscriptionSelector: FormControl<boolean>;
  subscriptionSelectorType: FormControl<SubscriptionSelectorType>;
  upgradeButtonLabel: FormControl<string>;
  upgradeButtonAppearance: FormControl<string>;
  upgradeButtonTextColor: FormControl<string>;
  // Stackable rule-based exceptions (PDQ-7681) — replaces the fixed gift/checkout/digital configs.
  exceptions: FormArray<FormGroup<ExceptionFormGroup>>;
}

export interface CartControlFormGroup {
  usageToken: FormControl<string>;
  internalName: FormControl<string>;
  internalDescription: FormControl<string>;
  configurations: FormGroup<ConfigurationsFormGroup>;
  displayRules: FormArray<FormGroup<TypedRuleGroup>>;
}

@Component({
  selector: 'cart-control-form',
  templateUrl: './cart-control-form.component.html',
  styleUrl: './cart-control-form.component.scss',
  standalone: false,
})
export class CartControlFormComponent implements OnInit {
  @Input() formGroup: FormGroup<CartControlFormGroup>;
  @Input() displayRules: DynamicRule[];
  @Input() displayRulesEntities: RuleEntity[];
  @Input() shopCurrencySymbol: string;

  tabs: LockableTabItem<CartControlFormTab>[] = [
    {
      label: 'Settings',
      value: CartControlFormTab.SETTINGS,
    },
    {
      label: 'Configurations',
      value: CartControlFormTab.CONFIGURATIONS,
    },
    {
      label: 'Display Conditions',
      value: CartControlFormTab.DISPLAY_CONDITIONS,
    },
    {
      label: 'Installation',
      value: CartControlFormTab.INSTALLATION,
    },
  ];

  CartControlFormTab = CartControlFormTab;
  activeTab = CartControlFormTab.SETTINGS;

  private featureAccess = inject(FeatureAccessService);

  ngOnInit() {
    this.tabs = this.featureAccess.lockTabOnFreePlan(this.tabs, CartControlFormTab.DISPLAY_CONDITIONS);
  }

  onTabChange(event: CartControlFormTab) {
    this.activeTab = event;
  }
}
